import { ChevronDown, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, NavLink, useLocation } from "react-router-dom";

import { useMediaQuery } from "@/lib/hooks/useMediaQuery";
import { useThemeStore } from "@/lib/theme/themeStore";

import { GymVizLogo } from "@/components/brand/GymVizLogo";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { firstAccessiblePath, visibleSections, workspaceForPath } from "@/app/layout/nav-config";
import type { NavSection } from "@/app/layout/nav-config";
import { useAuthStore } from "@/lib/auth/store";
import { useEffectivePermissions } from "@/lib/auth/permissions";
import { useNavStore } from "@/lib/nav/store";
import { cn } from "@/lib/utils/cn";

function isItemActive(to: string, pathname: string): boolean {
  if (to === "/") return pathname === "/";
  return pathname === to || pathname.startsWith(to + "/");
}

/** Skeumorphic nav item — raised pill when active, flat when not */
function NavItem({
  to,
  icon: Icon,
  labelKey,
  pathname,
}: {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  labelKey: string;
  pathname: string;
}) {
  const { t } = useTranslation();
  return (
    <NavLink
      to={to}
      end={to === "/"}
      aria-current={isItemActive(to, pathname) ? "page" : undefined}
      className={({ isActive }) =>
        cn(
          "group flex items-center gap-2.5 rounded-lg py-2 text-sm transition-all duration-200",
          "border-l-[3px] ps-2 pe-2.5",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1",
          isActive
            ? [
                // light — white raised pill with primary left accent
                "bg-gradient-to-b from-white to-slate-50/95",
                "shadow-[0_1px_4px_rgba(0,0,0,0.13),0_1px_2px_rgba(0,0,0,0.08),inset_0_1px_0_rgba(255,255,255,1)]",
                "border-l-primary-500 border-y-black/[0.07] border-r-black/[0.07]",
                "text-primary-700 font-semibold",
                "translate-x-0.5",
                // dark — primary tinted glow
                "dark:bg-primary-500/[0.18] dark:bg-none",
                "dark:shadow-[0_1px_8px_rgb(var(--color-primary-500)/0.30),inset_0_1px_0_rgba(255,255,255,0.08)]",
                "dark:border-l-primary-400 dark:border-y-primary-500/20 dark:border-r-primary-500/20",
                "dark:text-primary-300",
              ]
            : [
                "border-l-transparent border-y-transparent border-r-transparent",
                "text-ink-600 dark:text-ink-700",
                "hover:translate-x-0.5",
                "hover:bg-gradient-to-b hover:from-white/80 hover:to-ink-50/60",
                "dark:hover:bg-none dark:hover:bg-white/[0.06]",
                "hover:shadow-[0_1px_3px_rgba(0,0,0,0.07),inset_0_1px_0_rgba(255,255,255,0.8)]",
                "dark:hover:shadow-[0_1px_3px_rgba(0,0,0,0.4),inset_0_1px_0_rgba(255,255,255,0.05)]",
                "hover:border-l-primary-300 dark:hover:border-l-primary-600",
                "hover:border-y-black/[0.04] hover:border-r-black/[0.04]",
                "dark:hover:border-y-white/[0.07] dark:hover:border-r-white/[0.07]",
                "hover:text-ink-900 dark:hover:text-ink-100",
              ],
        )
      }
    >
      <Icon className="h-4 w-4 flex-shrink-0 transition-transform duration-200 group-hover:scale-110" aria-hidden="true" />
      <span>{t(labelKey)}</span>
    </NavLink>
  );
}

interface SectionGroupProps {
  section: NavSection;
  isOpen: boolean;
  onToggle: () => void;
  pathname: string;
}

function SectionGroup({ section, isOpen, onToggle, pathname }: SectionGroupProps) {
  const { t } = useTranslation();
  const hasActive = section.items.some((item) => isItemActive(item.to, pathname));

  return (
    <div className="mb-1">
      {/* Section header — embossed label row */}
      <button
        type="button"
        aria-expanded={isOpen}
        aria-controls={`nav-section-${section.key}`}
        onClick={onToggle}
        className={cn(
          "group flex w-full items-center justify-between rounded-lg px-2.5 py-1.5 text-left",
          "transition-all duration-200",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1",
          "hover:bg-ink-100/50 dark:hover:bg-white/[0.05]",
          "hover:translate-x-0.5",
        )}
      >
        <span
          className={cn(
            "text-[10.5px] font-bold uppercase tracking-widest select-none transition-colors duration-150",
            hasActive
              ? "text-primary-600 dark:text-primary-400"
              : "text-ink-400 dark:text-ink-500",
          )}
        >
          {t(section.labelKey)}
        </span>
        <ChevronDown
          className={cn(
            "h-3 w-3 transition-all duration-200 group-hover:scale-110",
            hasActive ? "text-primary-500 dark:text-primary-400" : "text-ink-400 dark:text-ink-500",
            isOpen && "rotate-180",
          )}
          aria-hidden="true"
        />
      </button>

      {/* Smooth height transition */}
      <div
        className={cn(
          "grid transition-all duration-200 motion-reduce:transition-none",
          isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
      >
        <div id={`nav-section-${section.key}`} role="region" className="overflow-hidden">
          <div className="pb-1 pt-0.5 space-y-0.5 px-1">
            {section.items.map((item) => (
              <NavItem
                key={item.to}
                to={item.to}
                icon={item.icon}
                labelKey={item.labelKey}
                pathname={pathname}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function Sidebar() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const isSuperuser = useAuthStore((s) => s.user?.is_superuser ?? false);
  const { byCode } = useEffectivePermissions();
  const { sidebarCollapsed, setSidebarCollapsed } = useNavStore();
  const { sidebarDark, sidebarBg, sidebarStroke } = useThemeStore();

  const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 1023px)");
  const effectivelyCollapsed = isTablet || sidebarCollapsed;

  const has = (key: string) => isSuperuser || key in byCode;
  const hasRef = useRef(has);
  hasRef.current = has;

  const activeWorkspace = workspaceForPath(pathname);
  const sections = activeWorkspace ? visibleSections(activeWorkspace.sections, has) : [];

  const [openSections, setOpenSections] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    for (const s of sections) {
      if (s.items.some((item) => isItemActive(item.to, pathname))) {
        initial.add(s.key);
      }
    }
    return initial;
  });

  // Auto-collapse on dashboard, auto-expand on every other route
  useEffect(() => {
    if (pathname === "/") {
      setSidebarCollapsed(true);
    } else {
      setSidebarCollapsed(false);
    }
  }, [pathname, setSidebarCollapsed]);

  // On non-dashboard routes: open ALL sections so every item is immediately visible
  useEffect(() => {
    const ws = workspaceForPath(pathname);
    if (!ws) return;
    const secs = visibleSections(ws.sections, hasRef.current);

    if (pathname !== "/") {
      // Open every section in the current workspace
      setOpenSections(new Set(secs.map((s) => s.key)));
    } else {
      // Dashboard: only keep the section containing the active item open (if any)
      setOpenSections((prev) => {
        const next = new Set(prev);
        let changed = false;
        for (const s of secs) {
          if (!next.has(s.key) && s.items.some((item) => isItemActive(item.to, pathname))) {
            next.add(s.key);
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }
  }, [pathname, activeWorkspace?.key]);

  function toggleSection(key: string) {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const asideBase =
    "hidden md:flex md:flex-col rounded-none border-y-0 border-s-0 border-e transition-[width] duration-200 motion-reduce:transition-none overflow-hidden h-dvh shrink-0 sticky top-0";

  const asideStyle: React.CSSProperties = {
    background: sidebarBg || "var(--color-sidebar-bg)",
    backdropFilter: "blur(18px) saturate(140%)",
    borderRightColor: sidebarStroke || "var(--color-sidebar-stroke)",
  };

  // ── Icon rail (collapsed / tablet) ──────────────────────────────────────
  if (effectivelyCollapsed) {
    return (
      <TooltipProvider delayDuration={200}>
        <aside className={cn(asideBase, "md:w-16", sidebarDark && "dark")} style={asideStyle}>
          <div className="flex flex-col items-center gap-2 py-4 px-2">
            <Link to="/"><GymVizLogo variant="mark" size={28} ariaLabel={t("app.name")} /></Link>
            <button
              type="button"
              aria-label={t("sidebar.expand")}
              onClick={() => setSidebarCollapsed(false)}
              className={cn(
                "mt-1 flex h-8 w-8 items-center justify-center rounded-lg",
                "text-ink-500 hover:bg-ink-100/60 dark:hover:bg-white/[0.06] hover:text-ink-800 dark:hover:text-ink-200",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
                "transition-all duration-150",
              )}
            >
              <PanelLeftOpen className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>

          <nav aria-label={t("sidebar.navAriaLabel")} className="flex-1 px-2 py-2 space-y-1 overflow-y-auto [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
            {sections.map((section) => {
              const SectionIcon = section.items[0]?.icon;
              const hasActive = section.items.some((item) => isItemActive(item.to, pathname));
              return (
                <div key={section.key} className="group relative">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        aria-label={t(section.labelKey)}
                        className={cn(
                          "flex h-9 w-9 mx-auto items-center justify-center rounded-lg transition-all duration-150",
                          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
                          hasActive
                            ? [
                                "bg-gradient-to-b from-white to-slate-50/95",
                                "shadow-[0_1px_4px_rgba(0,0,0,0.13),inset_0_1px_0_rgba(255,255,255,1)]",
                                "border border-black/[0.07]",
                                "text-primary-700",
                                "dark:bg-primary-500/[0.18] dark:bg-none",
                                "dark:shadow-[0_1px_8px_rgb(var(--color-primary-500)/0.30),inset_0_1px_0_rgba(255,255,255,0.08)]",
                                "dark:border-primary-500/20 dark:text-primary-300",
                              ]
                            : [
                                "border border-transparent",
                                "text-ink-500 dark:text-ink-200",
                                "hover:bg-ink-100/60 dark:hover:bg-white/[0.06]",
                                "hover:text-ink-800 dark:hover:text-ink-100",
                              ],
                        )}
                      >
                        {SectionIcon && <SectionIcon className="h-4 w-4" aria-hidden="true" />}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right">{t(section.labelKey)}</TooltipContent>
                  </Tooltip>

                  {/* Flyout panel */}
                  <div
                    className={cn(
                      "absolute left-full top-0 ms-2 z-50 min-w-[11rem]",
                      "rounded-xl border border-black/[0.07] dark:border-white/[0.10]",
                      "bg-white/90 dark:bg-ink-900/90 backdrop-blur-md",
                      "shadow-[0_4px_16px_rgba(0,0,0,0.12),0_1px_4px_rgba(0,0,0,0.08)]",
                      "dark:shadow-[0_4px_16px_rgba(0,0,0,0.5)]",
                      "py-1.5",
                      "opacity-0 pointer-events-none transition-[opacity,transform] duration-150",
                      "scale-95 origin-left",
                      "group-hover:opacity-100 group-hover:pointer-events-auto group-hover:scale-100",
                      "group-focus-within:opacity-100 group-focus-within:pointer-events-auto group-focus-within:scale-100",
                    )}
                  >
                    <div className="px-3 py-1 mb-1 text-[10px] font-bold uppercase tracking-widest text-ink-400 dark:text-ink-500 border-b border-black/[0.05] dark:border-white/[0.07]">
                      {t(section.labelKey)}
                    </div>
                    <div className="px-1.5 space-y-0.5">
                      {section.items.map((item) => (
                        <NavLink
                          key={item.to}
                          to={item.to}
                          end={item.to === "/"}
                          className={({ isActive }) =>
                            cn(
                              "flex items-center gap-2 rounded-lg py-2 text-sm transition-all duration-150",
                              "border-l-[3px] ps-2 pe-2.5",
                              "focus-visible:outline-none",
                              isActive
                                ? [
                                    "bg-gradient-to-b from-white to-slate-50/95",
                                    "shadow-[0_1px_3px_rgba(0,0,0,0.10),inset_0_1px_0_rgba(255,255,255,1)]",
                                    "border-l-primary-500 border-y-black/[0.07] border-r-black/[0.07]",
                                    "text-primary-700 font-semibold",
                                    "dark:bg-primary-500/[0.18] dark:bg-none",
                                    "dark:shadow-[0_1px_8px_rgb(var(--color-primary-500)/0.30),inset_0_1px_0_rgba(255,255,255,0.08)]",
                                    "dark:border-l-primary-400 dark:border-y-primary-500/20 dark:border-r-primary-500/20",
                                    "dark:text-primary-300",
                                  ]
                                : [
                                    "border-l-transparent border-y-transparent border-r-transparent",
                                    "text-ink-700 dark:text-ink-200",
                                    "hover:bg-ink-100/60 dark:hover:bg-white/[0.06]",
                                  ],
                            )
                          }
                        >
                          <item.icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                          <span>{t(item.labelKey)}</span>
                        </NavLink>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </nav>
        </aside>
      </TooltipProvider>
    );
  }

  // ── Expanded sidebar ─────────────────────────────────────────────────────
  return (
    <aside className={cn(asideBase, "md:w-64", sidebarDark && "dark")} style={asideStyle}>
      <div className="flex items-center justify-between px-4 py-4 pe-3">
        <Link to="/"><GymVizLogo variant="full" size={40} ariaLabel={t("app.name")} /></Link>
        <button
          type="button"
          aria-label={t("sidebar.collapse")}
          onClick={() => setSidebarCollapsed(true)}
          className={cn(
            "flex h-8 w-8 items-center justify-center rounded-lg",
            "text-ink-400 hover:bg-ink-100/60 dark:hover:bg-white/[0.06] hover:text-ink-700 dark:hover:text-ink-200",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
            "transition-all duration-150",
          )}
        >
          <PanelLeftClose className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      <nav aria-label={t("sidebar.navAriaLabel")} className="flex-1 px-2 py-2 overflow-y-auto [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
        {sections.map((section) => (
          <SectionGroup
            key={section.key}
            section={section}
            isOpen={openSections.has(section.key)}
            onToggle={() => toggleSection(section.key)}
            pathname={pathname}
          />
        ))}

        {sections.length === 0 && activeWorkspace && (
          <NavItem
            to={firstAccessiblePath(activeWorkspace, has)}
            icon={activeWorkspace.icon}
            labelKey={activeWorkspace.labelKey}
            pathname={pathname}
          />
        )}
      </nav>

      <div className="px-4 py-3 text-[11px] text-ink-400">v0.1.0</div>
    </aside>
  );
}
